import React, { useState, useEffect } from 'react';

const HUD = ({ gameState, currentUserId, showBombProgress }) => {
  const currentPlayer = gameState.entities[currentUserId];
  const activePlayers = gameState.activePlayers.length;
  const totalPlayers = Object.keys(gameState.entities).length;
  const bombDangerLevel = Math.min(100, Math.round((gameState.bomb.size / gameState.bomb.bombExplodeSize) * 100));
  
  return (
    <div
      className="absolute top-0 left-0 right-0 h-[54px] font-game"
      style={{ userSelect: 'none' }}
    >
      <div className="w-full h-full relative">
        <div className="w-full h-full absolute bg-gradient-to-b from-black to-black shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" />
        
        <div className="flex justify-between items-center relative h-full px-4">
          <div className="w-[218px] h-[26px] relative">
            <div className="w-[115px] h-[26px] absolute left-0 top-0 text-white text-xs font-bold font-[Atma] leading-[28px] [text-shadow:_0px_4px_4px_rgb(0_0_0_/_0.25)]">
              PLAYING AS :
            </div>
            <div className="w-[137px] h-[26px] absolute left-[81px] top-0 text-[#ff9400] text-xs font-bold font-[Atma] leading-[28px] [text-shadow:_0px_4px_4px_rgb(0_0_0_/_0.25)]">
              {currentPlayer?.name?.toUpperCase() || 'UNKNOWN'}
            </div>
          </div>
          
          <div className="w-[108px] h-[26px] text-white text-xs font-bold font-[Atma] leading-[28px] [text-shadow:_0px_4px_4px_rgb(0_0_0_/_0.25)]">
            PLAYERS : {activePlayers}/{totalPlayers}
          </div>
          
          {showBombProgress && (
            <div className="flex items-center gap-2 absolute left-1/2 transform -translate-x-1/2">
              <p>Bomb:</p>
              <div className="w-32 h-4 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${bombDangerLevel > 70 ? 'bg-red-600' : bombDangerLevel > 40 ? 'bg-orange-500' : 'bg-yellow-400'}`}
                  style={{ width: `${bombDangerLevel}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const GameOverModal = ({ results, onFinish }) => {
  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center pl-7 pr-[27px] pt-[152px] pb-[214px]">
      <div className="w-[335px] h-[478px] relative">
        <div className="w-[335px] h-[478px] left-0 top-0 absolute bg-white/80 rounded-[20px] shadow-[inset_0px_-4px_1px_0px_rgba(208,219,242,1.00)] border border-black" />
        
        <div className="w-[241px] h-[38px] left-[48px] top-[9px] absolute text-center text-[#434caf] text-[22px] font-extrabold font-['Dosis'] leading-[40px]">
          GAME OVER!
        </div>
        
        <div className="w-[269px] h-9 left-[20px] top-[47px] absolute">
          <span className="text-[#21ca51] text-xl font-extrabold font-['Dosis'] leading-[32px]">Winner: </span>
          <span className="text-[#ca3cee] text-xl font-extrabold font-['Dosis'] leading-[32px]">{results.winner}</span>
        </div>
        
        <div className="w-[239px] h-[297px] left-[18px] top-[88px] absolute">
          <div className="w-[237px] h-[27px] left-[2px] top-0 absolute text-[#434caf] text-lg font-extrabold font-['Dosis'] leading-[23px]">
            Leaderboard:
          </div>
          
          {results.eliminated.length > 0 ? (
            results.eliminated.map((player, index) => (
              <div 
                key={index}
                className="w-[188px] h-[27px] left-[2px] absolute text-[#434caf] text-sm font-extrabold font-['Dosis'] leading-[23px]"
                style={{ top: `${39 + index * 33}px` }}
              >
                {player}
              </div>
            ))
          ) : (
            <div className="w-[188px] h-[27px] left-[2px] top-[39px] absolute text-[#434caf] text-sm font-extrabold font-['Dosis'] leading-[23px]">
              None
            </div>
          )}
        </div>
        
        <div className="w-24 h-9 left-[119px] top-[411px] absolute">
          <div className="w-24 h-9 left-0 top-0 absolute bg-[#ac89ff] rounded-[20px] shadow-[inset_0px_-6px_2px_0px_rgba(116,91,173,1.00)] border-2 border-black" />
          <button
            className="w-24 h-[33px] left-0 top-0 absolute text-center text-white text-[22px] font-bold font-['Dosis'] capitalize leading-[32px] bg-transparent border-none cursor-pointer"
            onClick={onFinish}
          >
            Okay
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionButton = ({ label, emoji, onClick, cooldown, maxCooldown, disabled }) => {
  const cooldownPercentage = Math.min(100, (cooldown / maxCooldown) * 100);
  
  return (
    <div className="relative">
      <button
        className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl ${
          disabled ? 'bg-gray-500 opacity-50' : 'bg-blue-500 hover:bg-blue-600'
        }`}
        onClick={onClick}
        disabled={disabled}
      >
        {emoji}
      </button>
      {cooldown > 0 && (
        <div 
          className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center"
          style={{ clipPath: `polygon(50% 50%, 50% 0%, ${cooldownPercentage > 75 ? '100% 0%' : '50% 0%'}, ${cooldownPercentage > 50 ? '100% 100%' : '50% 0%'}, ${cooldownPercentage > 25 ? '0% 100%' : '50% 0%'}, ${cooldownPercentage > 0 ? '0% 0%' : '50% 0%'}, 50% 0%)` }}
        >
          <span className="text-white text-xs">{Math.ceil(cooldown / 100) / 10}s</span>
        </div>
      )}
      <div className="text-white text-center mt-1">{label}</div>
    </div>
  );
};

function GameUI({ gameState, gameInterface, onFinishGame }) {
  const [, forceUpdate] = useState();
  const [showBombProgress, setShowBombProgress] = useState(false);
  const currentUserId = gameInterface.userId;
  const currentPlayer = gameState.entities[currentUserId];
  
  const handlePass = (e) => {
    e.preventDefault();
    if (currentUserId && !gameState.isGameOver) {
      gameInterface.sendGameEvent('pass_bomb', { userId: currentUserId });
    }
  };
  
  const handleFakePass = (e) => {
    e.preventDefault();
    if (currentUserId && !gameState.isGameOver) {
      gameInterface.sendGameEvent('fake_pass', { userId: currentUserId });
    }
  };
  
  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate({});
    }, 10);
    return () => clearInterval(interval);
  }, []);
  
  // Add keyboard event listeners for Z and X keys
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key.toLowerCase() === 'z') {
        // Z key for Pass Bomb
        handlePass(e);
      } else if (e.key.toLowerCase() === 'x') {
        // X key for Fake Pass
        handleFakePass(e);
      } else if (e.key === '`') {
        // Toggle bomb progress bar visibility with ` key
        setShowBombProgress(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentUserId, gameState.isGameOver]); // Re-add listeners if these dependencies change
  
  // Allow players to attempt to pass even when they don't have the bomb
  // Only disable buttons if player is eliminated, bomb is animating/exploding, or game is over
  const canInteract = currentPlayer && 
                      !currentPlayer.isEliminated && 
                      !gameState.bomb.isAnimating && 
                      !gameState.bomb.isExploding && 
                      !gameState.isGameOver;

  return (
    <>
      <HUD gameState={gameState} currentUserId={currentUserId} showBombProgress={showBombProgress} />
      
      <div
        className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-8"
        style={{ userSelect: 'none' }}
      >
        <ActionButton 
          label="Pass (Z)"
          emoji="👉"
          onClick={handlePass}
          cooldown={currentPlayer?.passCooldown || 0}
          maxCooldown={1250} // Updated from 750 to 1250
          disabled={!canInteract || (currentPlayer?.passCooldown > 0)}
        />
        
        <ActionButton 
          label="Fake (X)"
          emoji="🤥"
          onClick={handleFakePass}
          cooldown={currentPlayer?.fakeCooldown || 0}
          maxCooldown={250}
          disabled={!canInteract || (currentPlayer?.fakeCooldown > 0)}
        />
      </div>
      
      {gameState.isGameOver && gameState.results && (
        <GameOverModal
          results={gameState.results}
          onFinish={onFinishGame}
        />
      )}
    </>
  );
}

export default GameUI;
